import { useEffect, useState } from 'react';
import { Building, Save, ShieldAlert, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Alert } from '../components/Alert';
import { Badge } from '../components/Badge';

export function SettingsPage() {
  const { activeOrg, activeRole, refreshSession } = useAuth();
  const [displayName, setDisplayName] = useState(activeOrg?.display_name || '');
  const [legalName, setLegalName] = useState(activeOrg?.legal_name || '');
  const [country, setCountry] = useState(activeOrg?.country || 'FR');
  const [address1, setAddress1] = useState(activeOrg?.address_line1 || '');
  const [address2, setAddress2] = useState(activeOrg?.address_line2 || '');
  const [postalCode, setPostalCode] = useState(activeOrg?.postal_code || '');
  const [city, setCity] = useState(activeOrg?.city || '');
  const [region, setRegion] = useState(activeOrg?.region || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isManager = activeRole === 'owner' || activeRole === 'admin';

  useEffect(() => {
    if (activeOrg) {
      setDisplayName(activeOrg.display_name || '');
      setLegalName(activeOrg.legal_name || '');
      setCountry(activeOrg.country || 'FR');
      setAddress1(activeOrg.address_line1 || '');
      setAddress2(activeOrg.address_line2 || '');
      setPostalCode(activeOrg.postal_code || '');
      setCity(activeOrg.city || '');
      setRegion(activeOrg.region || '');
    }
  }, [activeOrg]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrg?.id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.orgs.update(activeOrg.id, {
        display_name: displayName.trim(),
        legal_name: legalName.trim() || null,
        country,
        address_line1: address1.trim() || null,
        address_line2: address2.trim() || null,
        postal_code: postalCode.trim() || null,
        city: city.trim() || null,
        region: region.trim() || null,
      });
      await refreshSession();
      setSuccess('Organisation configuration saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxWidth: 800 }}>
      <div>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>Organisation Settings</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
          Tenant profile, jurisdiction, physical address, and CYRN Auth sync state.
        </p>
      </div>

      {error && (
        <Alert tone="danger" title="Update failed">
          {error}
        </Alert>
      )}

      {success && (
        <Alert tone="success" title="Settings updated">
          {success}
        </Alert>
      )}

      {/* Tenancy & Metadata Card */}
      <Card padding="var(--space-6)">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Building size={20} color="var(--accent)" />
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>General Profile</h2>
          </div>
          <Badge tone="accent">ID: {activeOrg?.id.slice(0, 8)}...</Badge>
        </div>

        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <Input
              label="Display Name *"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={!isManager || saving}
              required
            />
            <Input
              label="Immutable Slug"
              value={activeOrg?.slug || ''}
              disabled
              mono
              hint="Unique permanent tenant slug."
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <Input
              label="Legal Entity Name"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              disabled={!isManager || saving}
            />
            <Select
              label="Country / Jurisdiction"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={!isManager || saving}
              options={[
                { label: 'France (FR)', value: 'FR' },
                { label: 'Germany (DE)', value: 'DE' },
                { label: 'United Kingdom (GB)', value: 'GB' },
                { label: 'United States (US)', value: 'US' },
                { label: 'Switzerland (CH)', value: 'CH' },
                { label: 'Belgium (BE)', value: 'BE' },
                { label: 'Netherlands (NL)', value: 'NL' },
                { label: 'Spain (ES)', value: 'ES' },
                { label: 'Italy (IT)', value: 'IT' },
                { label: 'Other', value: 'ZZ' },
              ]}
            />
          </div>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 600 }}>
              Physical Postal Address
            </span>

            <Input
              label="Address Line 1"
              placeholder="10 rue de la Paix"
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              disabled={!isManager || saving}
            />
            <Input
              label="Address Line 2 (Optional)"
              placeholder="Bâtiment B, Étage 3"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              disabled={!isManager || saving}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
              <Input
                label="Postal Code"
                placeholder="75002"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                disabled={!isManager || saving}
              />
              <Input
                label="City"
                placeholder="Paris"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={!isManager || saving}
              />
              <Input
                label="Region / State"
                placeholder="Île-de-France"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={!isManager || saving}
              />
            </div>
          </div>

          {isManager && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
              <Button type="submit" variant="primary" disabled={saving} icon={<Save size={14} />}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </form>
      </Card>

      {/* Tenancy authority & CYRN Auth projection details */}
      <Card padding="var(--space-6)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Key size={20} color="var(--accent)" />
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Identity & Projection Authority</h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
          <p>
            Nexus is the single source of truth for tenancy (ADR-0003). Organisations are projected to CYRN Auth as OIDC org grants.
          </p>
          <div
            style={{
              padding: '12px',
              background: 'var(--bg-inset)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)',
              marginTop: 4,
            }}
          >
            <div>Organisation UUID: <span style={{ color: 'var(--text-primary)' }}>{activeOrg?.id}</span></div>
            <div>Tenancy Type: <span style={{ color: 'var(--accent)' }}>{activeOrg?.type}</span></div>
            <div>Creation Timestamp: <span style={{ color: 'var(--text-primary)' }}>{activeOrg?.created_at ? new Date(activeOrg.created_at).toISOString() : 'N/A'}</span></div>
          </div>
        </div>
      </Card>

      {/* Danger Zone: Only Owner can see */}
      {activeRole === 'owner' && (
        <Card padding="var(--space-6)" style={{ border: '1px solid rgba(255, 92, 92, 0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', marginBottom: 8 }}>
            <ShieldAlert size={20} />
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--danger)' }}>Organisation Closure</h2>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', lineHeight: 1.5 }}>
            Closing an organisation terminates all provisioned hosting and security services. In compliance with ADR-0004 & SPEC-01 §2.2, tenant records and audit logs are archived immutably.
          </p>
          <div style={{ marginTop: 16 }}>
            <Button
              variant="danger"
              size="sm"
              disabled
              title="Disabled - contact CYRN support for closure procedures"
            >
              Close Organisation (Disabled)
            </Button>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: 12 }}>
              Requires explicit verified support deprovisioning request
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}
